import { AccountInfo } from "@azure/msal-browser";
import Box from "../../../design/components-dev/BoxExtended";
import {
  BLUE_BACKGROUND,
  FloatingLayout as FloatingLayout,
  HEADER_SIZE,
  NextButtonJustifiedRight,
} from "../layout";
import { Alert, Checkbox, Chip, Divider, Typography, alpha } from "@mui/material";
import { getSizing } from "../../../design/sizing";
import { useXNGFormWithValidation } from "../../../hooks/useForm";
import { ServiceProviderType } from "../../../profile-sdk";
import {
  DistrictAccessStatus,
  GetClientDistrictsDistrictRef,
  GetClientsClientRef,
  GetStatesStateRef,
  ResponseError,
} from "@xng/xng-backend";
import * as yup from "yup";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { API_CLIENTS_BACKEND, API_USERS_BACKEND } from "../../../api/api";
import { placeholderForFutureLogErrorText } from "../../../temp/errorText";
import { useNavigate } from "react-router";
import { Controller } from "react-hook-form";
import { MSBInputErrorWrapper, MSBSearchMultiselect, MSBSearchSingleSelect } from "../../../fortitude";
import msbMUIAutoCompleteFilterOptions from "../../../utils/msb_mui_auto_complete_filter_options";
import useApiQueryGetServiceProviderTypesByDate from "../../../api/hooks/state_snapshots/use_api_query_get_service_provider_types_by_date";
import useApiQueryGetStates from "../../../api/hooks/states/use_api_query_get_states";
import useApiQueryGetClientsByState from "../../../api/hooks/clients/use_api_query_get_clients_by_state";
import useApiQueryGetClientDistricts from "../../../api/hooks/clients/use_api_query_get_client_districts";
import useApiQueryGetProviderTypes from "../../../api/hooks/user/use_api_query_get_provider_types";

const requestDistrictAccessFormValidation = yup.object().shape({
  state: yup
    .object<GetStatesStateRef>()
    .typeError("Please select your state")
    .required("Please select your state"),
  client: yup
    .object<GetClientsClientRef>()
    .typeError("Please select your client account")
    .required("Please select your client account"),
  districts: yup
    .array(yup.object<GetClientDistrictsDistrictRef>())
    .min(1, "Please select at least one district")
    .required("Please select at least one district")
    .typeError("Please select at least one district"),
  serviceProviderType: yup
    .array(yup.object<ServiceProviderType>())
    .min(1, "Please select your service provider type")
    .required("Please select your service provider type")
    .typeError("Please select your service provider type"),
});

interface RequestDistrictAccessForm {
  state: GetStatesStateRef;
  client: GetClientsClientRef;
  districts: GetClientDistrictsDistrictRef[];
  serviceProviderType: ServiceProviderType[];
}

/**
 * Where the user already stands with a district, or undefined when it is theirs to request. Drives
 * both the chip and the disabled state, so the two can't disagree.
 */
function districtStanding(
  district: GetClientDistrictsDistrictRef,
): { label: string; color: "success" | "warning" } | undefined {
  if (district.accessStatus === DistrictAccessStatus.Granted)
    return { label: "Granted", color: "success" };
  if (district.accessStatus === DistrictAccessStatus.Pending)
    return { label: "Pending", color: "warning" };
  return undefined;
}

function AccountRegistrationUserOnboardingFlow(props: {
  account: AccountInfo;
  /** Present only when the user chose to come here from their profile, so there is somewhere to go
   * back to. Absent during first-time onboarding, where the only ways out are submit or log out. */
  onCancel?: () => void;
}) {
  // HOOKS
  const navigate = useNavigate();

  // MSAL ACCOUNT ACCESSOR CONSTANTS
  if (props.account.idTokenClaims === undefined) throw new Error(placeholderForFutureLogErrorText);
  const _oid = props.account.idTokenClaims.oid;
  const _claimState = props.account.idTokenClaims.state as string | undefined;

  const [submitError, setSubmitError] = useState<string | null>(null);

  // ------------- XNG FORM -------------
  const {
    watch,
    setValue,
    control,
    trigger,
    handleSubmit,
  } = useXNGFormWithValidation<RequestDistrictAccessForm>({
    validationSchema: requestDistrictAccessFormValidation,
    defaultValues: {
      state: undefined,
      client: undefined,
      districts: [],
      serviceProviderType: [],
    },
  });

  const selectedState = watch("state");
  const selectedClient = watch("client");
  const selectedDistricts = watch("districts");

  // ------------- API -------------
  const statesQuery = useApiQueryGetStates();
  const stateOptions = useMemo(() => statesQuery.data?.states ?? [], [statesQuery.data]);

  const clientsQuery = useApiQueryGetClientsByState({
    queryParams: { state: selectedState?.abbreviation },
  });
  const clientOptions = clientsQuery.data?.clients ?? [];

  const districtsQuery = useApiQueryGetClientDistricts({
    queryParams: { clientId: selectedClient?.id },
  });
  const districtOptions = useMemo(
    () => districtsQuery.data?.districts ?? [],
    [districtsQuery.data],
  );

  // Provider types follow the *selected* state, not the token claim — a user who picks a different
  // state than the one on their claim would otherwise be offered the wrong state's provider types.
  const {
    data: serviceProviderTypesOptionsResponse,
    isError: serviceProviderTypesOptionsIsError,
    isPending: serviceProviderTypesOptionsIsPending,
    refetch: refetchMutlipleServiceProviderTypesOptions,
  } = useApiQueryGetServiceProviderTypesByDate({
    queryParams: { state: selectedState?.abbreviation ?? "" },
    options: { enabled: Boolean(selectedState?.abbreviation) },
  });

  const serviceProviderTypeOptions = useMemo(
    () => serviceProviderTypesOptionsResponse?.serviceProviderTypes ?? [],
    [serviceProviderTypesOptionsResponse],
  );

  // What the user already has. Submitting reconciles the picker against this list and deletes
  // whatever is missing from it, so the picker has to start out reflecting it — otherwise anyone
  // who comes here to change their districts silently loses every type they do not re-pick.
  const currentProviderTypesQuery = useApiQueryGetProviderTypes({ queryParams: { id: _oid! } });
  const currentProviderTypes = useMemo(
    () => currentProviderTypesQuery.data ?? [],
    [currentProviderTypesQuery.data],
  );

  // Seeded once per state, not once ever: the options are state-scoped, so a state change has to
  // re-resolve the user's types against the new list. Tracking the state it was seeded for also
  // keeps this from fighting the user when they deliberately clear a selection.
  const seededForState = useRef<string | undefined>(undefined);
  useEffect(() => {
    const abbreviation = selectedState?.abbreviation;
    if (!abbreviation || serviceProviderTypeOptions.length === 0) return;
    if (seededForState.current === abbreviation) return;
    seededForState.current = abbreviation;

    // ProviderTypeRecord.id is a number and the option's is a string — compare as strings.
    const held = new Set(currentProviderTypes.map((t) => String(t.id)));
    const alreadyHeld = serviceProviderTypeOptions.filter((option) => held.has(String(option.id)));
    if (alreadyHeld.length > 0) setValue("serviceProviderType", alreadyHeld);
  }, [selectedState, serviceProviderTypeOptions, currentProviderTypes, setValue]);

  // Default to the state on the token when we operate there, so the common case is one fewer click.
  useEffect(() => {
    if (selectedState || !_claimState) return;
    const match = stateOptions.find((s) => s.abbreviation === _claimState);
    if (match) setValue("state", match);
  }, [stateOptions, selectedState, _claimState, setValue]);

  // The districts actually open to a request — what "Select all" means here, what gets pre-checked,
  // and what tells us there is nothing left to ask for.
  const selectableDistricts = useMemo(
    () => districtOptions.filter((d) => !districtStanding(d)),
    [districtOptions],
  );

  const everyDistrictTaken = districtOptions.length > 0 && selectableDistricts.length === 0;

  // Pre-check the district when there is only one to pick — the single-district account case.
  useEffect(() => {
    if (selectedDistricts?.length) return;
    if (selectableDistricts.length === 1) {
      setValue("districts", selectableDistricts);
    }
  }, [selectableDistricts, selectedDistricts, setValue]);

  // ------------- ON SUBMIT -------------
  async function onSubmit(data: RequestDistrictAccessForm) {
    setSubmitError(null);

    // Step 1: sync the user's provider types (user-level, independent of the districts requested)
    const currentTypeIds = new Set(currentProviderTypes.map((t) => String(t.id)));
    const selectedTypeIds = new Set((data.serviceProviderType ?? []).map((t) => String(t.id)));

    // Only remove what the user could actually see. The options are scoped to the selected state and
    // to today's date, so a type can be held but not offered — a retired one, or one belonging to a
    // state the user is moving away from. Deselecting is a decision; never being shown the option is
    // not, and treating the two the same silently strips credentials. This also fails safe: if the
    // options never load, nothing is deleted.
    const offeredTypeIds = new Set(serviceProviderTypeOptions.map((o) => String(o.id)));

    await Promise.all([
      ...currentProviderTypes
        .filter(
          (t) =>
            t.id != null &&
            offeredTypeIds.has(String(t.id)) &&
            !selectedTypeIds.has(String(t.id)),
        )
        .map((t) =>
          API_USERS_BACKEND.deleteProviderType({
            id: _oid!,
            providerType: String(t.id),
          }),
        ),
      ...(data.serviceProviderType ?? [])
        .filter((t) => t.id != null && !currentTypeIds.has(String(t.id)))
        .map((t) =>
          API_USERS_BACKEND.postProviderType({
            id: _oid!,
            postProviderTypeBody: { serviceProviderType: Number(t.id) },
          }),
        ),
    ]);

    // Step 2: request the districts. One call, one transaction — the whole selection reaches the
    // admin's queue or none of it does, so there is no partial state to reconcile here.
    try {
      await API_CLIENTS_BACKEND.postClientAccessRequests({
        clientId: data.client.id!,
        postClientAccessRequestsBody: {
          districtIds: data.districts.map((d) => d.id!),
        },
      });
    } catch (error) {
      // A 404 means a district we offered is no longer active in the account — our list is stale
      // rather than the user being wrong, so refresh it and let them resubmit.
      if (error instanceof ResponseError && error.response?.status === 404) {
        await districtsQuery.refetch();
        setValue("districts", []);
        setSubmitError(
          "That list of districts was out of date. We've refreshed it — please choose again.",
        );
        return;
      }
      setSubmitError("We couldn't submit your request. Please try again.");
      return;
    }

    // Step 3: reload into the post-request state
    navigate(0);
  }

  return (
    <Box sx={BLUE_BACKGROUND}>
      <FloatingLayout>
        <Typography variant={HEADER_SIZE}>Request District Access</Typography>
        <Typography sx={{ marginTop: getSizing(2) }} variant="body1">
          It looks like your email address is not associated with a district account. Please select
          the districts you work in below to request access from your district administrator.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: getSizing(3),
            paddingTop: getSizing(4),
            paddingBottom: getSizing(2),
          }}
        >
          <Controller
            name="state"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <MSBInputErrorWrapper
                isError={statesQuery.isError || !!error?.message}
                displayRefreshButton={statesQuery.isError}
                errorText={(() => {
                  if (statesQuery.isError)
                    return "Failed to load states, please click refresh icon to retry";
                  return error?.message ?? "";
                })()}
                refetch={statesQuery.refetch}
              >
                <MSBSearchSingleSelect
                  selectedOption={field.value ?? null}
                  options={stateOptions}
                  getOptionLabel={(option) => `${option?.name}`}
                  onChange={(newState) => {
                    field.onChange(newState);
                    // Everything downstream is scoped to the state.
                    setValue("client", undefined as unknown as GetClientsClientRef);
                    setValue("districts", []);
                    setValue("serviceProviderType", []);
                  }}
                  label={statesQuery.isPending ? "Loading States..." : "State"}
                  autocompleteProps={{
                    fullWidth: true,
                    disabled: statesQuery.isPending || statesQuery.isError,
                    filterOptions: msbMUIAutoCompleteFilterOptions(),
                    isOptionEqualToValue: (option, value) =>
                      option.abbreviation === value.abbreviation,
                  }}
                />
              </MSBInputErrorWrapper>
            )}
          />

          <Controller
            name="client"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <MSBInputErrorWrapper
                isError={clientsQuery.isError || !!error?.message}
                displayRefreshButton={clientsQuery.isError}
                errorText={(() => {
                  if (clientsQuery.isError)
                    return "Failed to load accounts, please click refresh icon to retry";
                  return error?.message ?? "";
                })()}
                refetch={clientsQuery.refetch}
              >
                <MSBSearchSingleSelect
                  selectedOption={field.value ?? null}
                  options={clientOptions}
                  getOptionLabel={(option) => `${option?.name}`}
                  onChange={(newClient) => {
                    field.onChange(newClient);
                    setValue("districts", []);
                  }}
                  label={(() => {
                    if (!selectedState) return "Select a state first";
                    if (clientsQuery.isFetching) return "Loading Client Accounts...";
                    return "Client Account";
                  })()}
                  autocompleteProps={{
                    fullWidth: true,
                    disabled: !selectedState || clientsQuery.isFetching || clientsQuery.isError,
                    filterOptions: msbMUIAutoCompleteFilterOptions(),
                    isOptionEqualToValue: (option, value) => option.id === value.id,
                  }}
                />
              </MSBInputErrorWrapper>
            )}
          />

          <Controller
            name="districts"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <MSBInputErrorWrapper
                isError={districtsQuery.isError || !!error?.message}
                displayRefreshButton={districtsQuery.isError}
                errorText={(() => {
                  if (districtsQuery.isError)
                    return "Failed to load districts, please click refresh icon to retry";
                  return error?.message ?? "";
                })()}
                refetch={districtsQuery.refetch}
                formHelperTextProps={{ sx: { position: "relative" } }}
              >
                <MSBSearchMultiselect
                  selectedOptions={field.value || []}
                  options={districtOptions}
                  getOptionLabel={(option) => `${option?.name}`}
                  onChange={(newDistricts) => setValue("districts", newDistricts)}
                  renderOptionVariant="checkbox"
                  variant="no overflow after 1"
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  label={(() => {
                    if (!selectedClient) return "Select a client account first";
                    if (districtsQuery.isFetching) return "Loading Districts...";
                    return "District(s)";
                  })()}
                  // Stays open even when nothing is selectable: the per-district Granted / Pending
                  // reasons are the answer to "where do I already stand?", and disabling the control
                  // would hide them at the one moment they matter. getOptionDisabled below is what
                  // stops a taken district being picked.
                  disabled={!selectedClient || districtsQuery.isFetching || districtsQuery.isError}
                  autocompleteProps={{
                    disableCloseOnSelect: true,
                    filterOptions: msbMUIAutoCompleteFilterOptions(),
                    // Districts you already hold, or have already asked for, stay visible so the
                    // absence of a district isn't mistaken for the list being wrong.
                    getOptionDisabled: (option) => Boolean(districtStanding(option)),
                    renderOption: (optionProps, option, state) => {
                      const { key, ...liProps } = optionProps as typeof optionProps & { key: string };
                      const standing = districtStanding(option);
                      const chosen = field.value ?? [];
                      const allSelectableChosen =
                        selectableDistricts.length > 0 &&
                        selectableDistricts.every((d) => chosen.some((c) => c.id === d.id));
                      return (
                        <Fragment key={key}>
                          {state.index === 0 && selectableDistricts.length > 1 && (
                            <>
                              <Box
                                onClick={() =>
                                  setValue(
                                    "districts",
                                    allSelectableChosen ? [] : selectableDistricts,
                                  )
                                }
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  px: 2,
                                  cursor: "pointer",
                                }}
                              >
                                <Checkbox size="small" checked={allSelectableChosen} />
                                <Typography variant="body2">Select all</Typography>
                              </Box>
                              <Divider />
                            </>
                          )}
                        <li
                          {...liProps}
                          // MUI dims the whole option when it is disabled, and CSS opacity on a
                          // parent cannot be undone by a child — it would take the status chip down
                          // with the row. Keep the row at full opacity and mute the checkbox and
                          // name individually, so the chip stays legible.
                          style={{
                            ...liProps.style,
                            opacity: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Checkbox size="small" checked={state.selected} disabled={Boolean(standing)} />
                          <Typography
                            variant="body2"
                            sx={{ flex: 1, color: standing ? "text.disabled" : "text.primary" }}
                          >
                            {option.name}
                          </Typography>
                          {standing && (
                            <Chip
                              size="small"
                              label={standing.label}
                              sx={{
                                border: "none",
                                fontWeight: 500,
                                bgcolor: (theme) => alpha(theme.palette[standing.color].main, 0.14),
                                color: (theme) => theme.palette[standing.color].dark,
                              }}
                            />
                          )}
                        </li>
                        </Fragment>
                      );
                    },
                  }}
                  sx={{ flex: 1 }}
                />
              </MSBInputErrorWrapper>
            )}
          />

          {everyDistrictTaken && (
            <Alert severity="info">
              You already have access to, or have already requested, every district in this account.
            </Alert>
          )}

          <Controller
            name="serviceProviderType"
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <MSBInputErrorWrapper
                  isError={serviceProviderTypesOptionsIsError || !!error?.message}
                  displayRefreshButton={serviceProviderTypesOptionsIsError}
                  errorText={(() => {
                    if (serviceProviderTypesOptionsIsError)
                      return "Failed to load Service Provider Types, please click refresh icon to retry";
                    if (error?.message) return error.message;
                    return "";
                  })()}
                  refetch={refetchMutlipleServiceProviderTypesOptions}
                  formHelperTextProps={{ sx: { position: "relative" } }}
                >
                  <MSBSearchMultiselect
                    selectedOptions={field.value || []}
                    options={serviceProviderTypeOptions}
                    getOptionLabel={(option) => `${option?.name}`}
                    onChange={(newServiceProviderTypes) => {
                      setValue("serviceProviderType", newServiceProviderTypes);
                    }}
                    renderOptionVariant="checkbox"
                    variant="no overflow after 1"
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    label={(() => {
                      if (!selectedState) return "Select a state first";
                      if (serviceProviderTypesOptionsIsPending) {
                        return "Loading Service Provider Types Options...";
                      }
                      if (serviceProviderTypesOptionsIsError) {
                        return "Failed to load Service Provider Types Options";
                      }
                      return "Service Provider Type(s)";
                    })()}
                    disabled={
                      !selectedState ||
                      serviceProviderTypesOptionsIsPending ||
                      serviceProviderTypesOptionsIsError
                    }
                    autocompleteProps={{
                      disableCloseOnSelect: true,
                      filterOptions: msbMUIAutoCompleteFilterOptions(),
                    }}
                    sx={{ flex: 1 }}
                  />
                </MSBInputErrorWrapper>
              );
            }}
          />

          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Box>

        <NextButtonJustifiedRight
          onSkip={props.onCancel}
          overrideSkipText="Cancel"
          onNext={async () => {
            const valid = await trigger();
            if (valid) {
              handleSubmit(onSubmit)();
            }
          }}
        />
      </FloatingLayout>
    </Box>
  );
}

export default AccountRegistrationUserOnboardingFlow;
